import { api } from '@/services';
import { APP_NAME } from '@/constants';
import { is401, getErrorDetails } from '@/utils';

class AuthorizationException extends Error {
  constructor() {
    super('Failed to obtain Music User Token');
  }
}

const loadLibrary = () => {
  return new Promise((resolve, reject) => {
    if (window.MusicKit) {
      return resolve();
    }

    const script = document.createElement('script');
    script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
    script.onload = () => document.addEventListener('musickitloaded', resolve);
    script.onerror = () => reject(new Error('Failed to load MusicKit.js.'));
    document.head.appendChild(script);
  });
};

export const configure = async () => {
  await loadLibrary();

  if (!MusicKit.getInstance()) {
    const { token: developerToken } = await api.getAppleToken();

    await MusicKit.configure({
      app: {
        name: APP_NAME,
        build: '1.0.0',
      },
      developerToken,
    });
  }

  return MusicKit.getInstance();
};

export const getUserName = () => 'Apple Music';

const syncConnection = async (musicUserToken) => {
  await api.connectApple(musicUserToken);

  return api.getAppleStatus();
};

export const login = async (updateAuthState, onUnauthorized) => {
  updateAuthState({ isSigningIn: true });

  try {
    const musicKit = await configure();

    const musicUserToken = await musicKit.authorize();

    if (!musicUserToken) {
      throw new AuthorizationException();
    }

    const accounts = await syncConnection(musicUserToken);

    updateAuthState({ accounts, error: null, isSigningIn: false });
  } catch (error) {
    const { is429, message } = getErrorDetails(error);

    if (is429) {
      updateAuthState({ error: message, isSigningIn: false });
      return;
    }

    updateAuthState({ isSigningIn: false });

    if (is401(error)) onUnauthorized();

    console.error('Error logging into Apple Music:', error);
  }
};

export const refresh = async (updateAuthState, onUnauthorized) => {
  updateAuthState({ isSigningIn: true });

  try {
    const musicKit = await configure();

    if (!musicKit.isAuthorized) {
      // Apple connections are tied to the live MusicKit session (no refresh
      // token). Without an active browser session, persisted accounts are
      // unusable, so don't surface them.
      updateAuthState({ error: null, accounts: [], isSigningIn: false });

      return;
    }

    const musicUserToken = await musicKit.authorize();

    if (!musicUserToken) {
      throw new AuthorizationException();
    }

    const accounts = await syncConnection(musicUserToken);

    updateAuthState({ accounts, error: null, isSigningIn: false });
  } catch (error) {
    const { is429, message } = getErrorDetails(error);

    if (is429) {
      updateAuthState({ error: message, isSigningIn: false });
      return;
    }

    updateAuthState({ accounts: [], isSigningIn: false });

    if (is401(error)) onUnauthorized();

    console.error('Failed to refresh Apple Music session:', error);
  }
};

export const logout = async (updateAuthState, onUnauthorized, accountId) => {
  updateAuthState({ isSigningOut: true });

  try {
    if (accountId) {
      await api.disconnectAppleAccount(accountId);
    }

    const musicKit = window.MusicKit?.getInstance();

    if (musicKit?.isAuthorized) {
      await musicKit.unauthorize();
    }
  } catch (error) {
    if (is401(error)) onUnauthorized();

    console.error('Error logging out of Apple Music:', error);
  } finally {
    await refresh(updateAuthState, onUnauthorized);

    updateAuthState({ isSigningOut: false });
  }
};
